import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Button, Grid, TextField } from "@mui/material";

import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import { getOptionValue } from "common/enums";

import {
  useGetMenuContentQuery,
  useLazyGetMenuDetailsById,
  usePostMenuItemsMutation,
} from "apis/HomePage";

import "react-quill/dist/quill.snow.css";

const colors = [
  "red",
  "#785412",
  "#ff7f50",
  "#87cefa",
  "#da70d6",
  "#32cd32",
  "#6495ed",
  "#ff69b4",
  "#ba55d3",
  "#cd5c5c",
  "#ffa500",
  "#40e0d0",
  "#fff",
  "#121212",
  "#ffff00",
];

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ size: [] }],
    [{ font: [] }],
    [{ align: ["right", "center", "justify"] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    [
      {
        color: colors,
      },
    ],
    [
      {
        background: colors,
      },
    ],
  ],
};

const HomePageContent = ({ t }) => {
  const {
    data: { data: menuItemByContentTypeData } = {},
    isLoading: isContentLoading,
  } = useGetMenuContentQuery({
    contentType: getOptionValue("homeContentType", "Text"),
  });

  const [
    getMenuDetailsById,
    {
      data: { data: { id, isHeader, name, parent } = {} } = {},
      isLoading: isMenuDataLoading,
      isFetching,
    },
  ] = useLazyGetMenuDetailsById();
  const [updateMenuItems, { isLoading }] = usePostMenuItemsMutation();

  const [htmlContent, setHtmlContent] = useState();
  const [selectedContentType, setSelectedContentType] = useState("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const updateBtnHandler = async () => {
    try {
      const formData = new FormData();

      formData.append("id", id);
      formData.append("isHeader", isHeader);
      formData.append("name", name);
      formData.append("contentType", getOptionValue("homeContentType", "Text"));
      if (parent?.id) {
        formData.append("parentId", parent?.id);
      }
      formData.append("content", htmlContent.toString("html"));
      await updateMenuItems(formData);
      setIsConfirmationModalOpen(false);
    } catch (error) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (selectedContentType) {
          const {
            data: { content },
          } = await getMenuDetailsById(selectedContentType);

          setHtmlContent(content);
        }
      } catch (error) {}
    })();
  }, [selectedContentType]);

  const updatedContentTypesOptions = useMemo(
    () =>
      menuItemByContentTypeData?.length
        ? menuItemByContentTypeData.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [menuItemByContentTypeData]
  );

  useEffect(() => {
    if (updatedContentTypesOptions.length) {
      setSelectedContentType(updatedContentTypesOptions[0].value);
    }
  }, [updatedContentTypesOptions]);

  return (
    <>
      <Backdrop
        open={isLoading || isMenuDataLoading || isContentLoading || isFetching}
      />
      <BreadCrumb title={t("Home Page Content")} />
      <ConfirmationDialog
        isOpen={isConfirmationModalOpen}
        title="Warning"
        message="Are you sure you want to update the content?"
        onCancel={() => setIsConfirmationModalOpen(false)}
        onConfirm={updateBtnHandler}
        isLoading={isLoading}
        variant="warning"
      />
      <Grid container>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={1}
          item
          xs={12}
          sm={9}
          md={10}
        >
          <Grid item xs={8} sm={5} md={6} lg={4}>
            <SelectField
              value={
                updatedContentTypesOptions.find(
                  ({ value }) => value === selectedContentType
                ) ?? ""
              }
              onChange={(_, { value }) => setSelectedContentType(value)}
              options={updatedContentTypesOptions}
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return (
                  <TextField {...params} label={t("Select Content Type")} />
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={4} sm={3} md={2} className="d-flex justify-content-end">
          <Button
            sx={{ maxHeight: "40px" }}
            variant="contained"
            disabled={!selectedContentType || isLoading}
            color="primary"
            onClick={() => setIsConfirmationModalOpen(true)}
          >
            {t("Save")}
          </Button>
        </Grid>
      </Grid>

      <ReactQuill
        value={htmlContent}
        theme="snow"
        onChange={(value) => setHtmlContent(value)}
        modules={modules}
      />
    </>
  );
};

export default withTranslation()(HomePageContent);

HomePageContent.propTypes = {
  t: PropTypes.func,
};
